import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { UnauthenticatedPurchasesService } from '@app/core/services/unauthenticated-purchases.service';
import { environment } from '@env';
import { Platform } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { OrderDTO, OrderService, PageOrderDTO } from 'ticket-sdk';

interface YearGroup {
  year: number,
  monthGroups: MonthGroup[]
}

interface MonthGroup {
  month: number,
  orders: OrderDTO[];
}

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.page.html',
  styleUrls: ['./order-history.page.scss'],
})
export class OrderHistoryPage implements OnInit, OnDestroy {

  fetchedOrders: number = 0;
  isAuthenticated: Observable<boolean>;
  isLoading: boolean = false;
  maxOrders: number = 0;
  pageNumber: number = 0;
  pageSize: number = 15;

  yearGroups: YearGroup[] = [];

  private onDestroy = new Subject();

  constructor(
    private orderService: OrderService,
    private router: Router,
    public platform: Platform,
    private authenticationService: AuthenticationService,
    private unauthenticatedPurchasesService: UnauthenticatedPurchasesService,
  ) { }

  get areAllOrdersFetched() {
    return this.fetchedOrders >= this.maxOrders;
  }

  get isMobile() {
    return this.platform.is('mobile');
  }

  get isProd(): boolean {
    return environment.production;
  }
  
  ngOnInit() {
    this.isAuthenticated = this.authenticationService.authenticationState;

    this.platform.ready().then(() => {
      if (! this.isMobile) {
        this.pageSize = 20;
      }

      this.isLoading = true;

    });
  }

  ionViewDidEnter() {
    this.authenticationService.checkAuthentication().pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.fetchOrders().pipe(take(1)).subscribe();
      }
      else {
        this.unauthenticatedPurchasesService.getUnauthenticatedPurchases()
        .pipe(
          takeUntil(this.onDestroy),
          map(orders => {
            this.yearGroups = [];
            this.fetchedOrders = 0;
            this.pageNumber = 0;
            this.maxOrders = orders.length;
            this.isLoading = false;

            return orders;
          })
        )
        .subscribe(orders => {
          this.processUnauthenticatedPurchases(orders);
        });
      }
    });
  }

  addOrdersToGroups(orders: OrderDTO[]) {
    orders.forEach(order => {
      let yearGroup: YearGroup = this.yearGroups.find(yearGroup => yearGroup.year === order.createdDate.getFullYear());
      if (yearGroup) {
        let monthGroup: MonthGroup = yearGroup.monthGroups.find(monthGroup => monthGroup.month === order.createdDate.getMonth());
        if (monthGroup) {
          if (! monthGroup.orders.find(o => o.orderId === order.orderId)) {
            monthGroup.orders.push(order);
          }
        }
        else {
          monthGroup = {month: order.createdDate.getMonth(), orders: [order]};
          yearGroup.monthGroups.push(monthGroup);
        }
      }
      else {
        let monthGroup: MonthGroup = {month: order.createdDate.getMonth(), orders: [order]};
        yearGroup = {year: order.createdDate.getFullYear(), monthGroups: [monthGroup]};
        this.yearGroups.push(yearGroup);
      }
    });
  }

  fetchOrders(): Observable<PageOrderDTO> {
    return this.orderService.getUserOrders(this.pageNumber, this.pageSize, ['createdDate,desc'])
    .pipe(
      take(1),
      tap(ordersPage => {
        this.addOrdersToGroups(ordersPage.content);
        this.fetchedOrders += ordersPage.content.length;
        this.pageNumber++;
        this.maxOrders = ordersPage.totalElements;
        this.isLoading = false;
      })
    )
  }

  getMonthName(month: number) {
    switch (month) {
      case 0:
        return 'Januari'
      case 1:
        return 'Februari'
      case 2:
        return 'Mars'
      case 3:
        return 'April'
      case 4:
        return 'Maj'
      case 5:
        return 'Juni'
      case 6:
        return 'Juli'
      case 7:
        return 'Augusti'
      case 8:
        return 'September'
      case 9:
        return 'Oktober'
      case 10:
        return 'November'
      case 11:
        return 'December'
      default:
        return 'Januari'
    }
  }

  getReceipt(order: OrderDTO) {
    this.router.navigateByUrl('receipt/' + order.externalOrderId1);
  }

  onBottomScroll(event) {
    this.isAuthenticated.pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.fetchOrders().pipe(take(1)).subscribe(orders => {
          event.target.complete();
        });
      }
      else {
        this.unauthenticatedPurchasesService.getUnauthenticatedPurchases()
        .pipe(take(1))
        .subscribe(orders => {
          this.processUnauthenticatedPurchases(orders);
          event.target.complete();
        });
      }
    });
  }

  processUnauthenticatedPurchases(orders) {
    if (orders.length > 0) {
      orders.sort((a, b) => a.createdDate > b.createdDate ? -1 : 1);

      let startingIndex = this.pageNumber * this.pageSize;
      let endingIndex = orders.length >= (this.pageSize + this.fetchedOrders + 1) ? (startingIndex + this.pageSize) : (orders.length);
      orders = orders.slice(startingIndex, endingIndex);

      this.addOrdersToGroups(orders);
      this.fetchedOrders += this.pageSize;
      this.pageNumber++;
    }
  }

  trimVehicleTypeDescription(vehicleTypeDescription: string): string {
    return vehicleTypeDescription.replace(' 0-6m', '');
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
  }
}
